import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./Login.css";
import logo from "../../../Assets/LOGO-GENYDMS(2).png";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ── State passed from Login ──
  const fromLogin      = location.state?.username_verified || false;
  const passedUsername = location.state?.username          || "";
  const maskedEmail    = location.state?.prefilled_email   || ""; // display only — never sent to backend

  // Direct flow: user types their own real email
  const [email, setEmail]         = useState("");
  const [loading, setLoading]     = useState(false);
  const [sent, setSent]           = useState(false);
  const [sentTo, setSentTo]       = useState("");
  const [error, setError]         = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const triggerPopup = (msg) => {
    setError(msg);
    setShowPopup(true);
  };

  const handleSendLink = async () => {
    setLoading(true);
    try {

      if (fromLogin && passedUsername) {
        // ── FLOW A: Came from Login with verified username ───────────────────
        // Send the USERNAME — backend looks up the real email from DB itself.
        // The masked email is NEVER sent — it would fail the DB lookup.
        await fetch(`${BACKEND}/api/webmanager/forgot-password/send-link-by-username`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ username: passedUsername }),
        });

        setSentTo(maskedEmail || "your registered email");
        setSent(true);

      } else {
        // ── FLOW B: User arrived directly at /forgot-password ────────────────
        // They enter their real email address directly.
        const trimmed = email.trim();

        if (!trimmed) {
          triggerPopup("Please enter your registered email address.");
          setLoading(false);
          return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          triggerPopup("Please enter a valid email address.");
          setLoading(false);
          return;
        }

        await fetch(`${BACKEND}/api/webmanager/forgot-password/send-link`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ email: trimmed }),
        });

        setSentTo(trimmed);
        setSent(true);
      }

    } catch {
      triggerPopup("Unable to connect to the server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-0 login-container">
      <div className="row g-0 min-vh-100">

        {/* LEFT PANEL */}
        <div className="col-md-6 d-flex flex-column justify-content-center align-items-center text-white left-panel">
          <h1 className="welcome-heading">Forgot Your Password?</h1>
          <div />
          <h1 className="welcome-heading">
            We'll send a <span>secure reset link</span> to your email.
          </h1>
          <div>
            <p>
              Enter your registered email address and we'll send you a link
              <br /> to securely reset your password.
            </p>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="col-md-6 d-flex justify-content-center align-items-center right-panel">
          <div className="card shadow border-0" style={{ width: "360px" }}>

            <div
              className="card-header text-center text-white"
              style={{ backgroundColor: "#001F54" }}
            >
              Password Recovery
            </div>

            <div className="card-body px-3 py-3">

              {/* LOGO */}
              <div className="logo-wrapper mx-auto mb-3">
                <img src={logo} alt="Logo" className="logo-img" />
              </div>

              {!sent ? (
                <>
                  {fromLogin && passedUsername ? (
                    /* ── FLOW A UI: Username already verified — just confirm and send ── */
                    <>
                      {/* Verified username badge */}
                      <div
                        className="alert alert-success py-2 px-3 mb-2 d-flex align-items-center gap-2"
                        style={{ fontSize: "0.82rem" }}
                      >
                        <span></span>
                        <span>
                          Username <strong>{passedUsername}</strong> verified.
                        </span>
                      </div>

                      {/* Masked email — display only, never sent to backend */}
                      {maskedEmail && (
                        <div
                          className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                          style={{ fontSize: "0.82rem" }}
                        >
                          <span>📧</span>
                          <span>
                            Reset link will be sent to <strong>{maskedEmail}</strong>
                          </span>
                        </div>
                      )}

                      <p className="text-muted small mb-3" style={{ fontSize: "0.78rem" }}>
                        Click below to send a password reset link to your registered email address.
                      </p>

                      <button
                        type="button"
                        className="btn btn-sm w-100 text-white d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={handleSendLink}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Sending...
                          </>
                        ) : (
                          "Send Reset Link"
                        )}
                      </button>
                    </>
                  ) : (
                    /* ── FLOW B UI: Direct access — user types real email ── */
                    <>
                      <label className="form-label fw-bold mb-1">REGISTERED EMAIL:</label>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        placeholder="Enter your registered email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && !loading && handleSendLink()}
                        autoFocus
                        disabled={loading}
                      />

                      <button
                        type="button"
                        className="btn btn-sm w-100 mt-3 text-white d-flex align-items-center justify-content-center gap-2"
                        style={{ backgroundColor: "#001F54" }}
                        onClick={handleSendLink}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm" role="status" />
                            Sending...
                          </>
                        ) : (
                          "Send Reset Link"
                        )}
                      </button>
                    </>
                  )}

                  <div className="text-center mt-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{ color: "#001F54", fontSize: "0.82rem" }}
                      onClick={() => navigate("/login")}
                    >
                      ← Back to Login
                    </button>
                  </div>
                </>
              ) : (
                /* ── SUCCESS STATE ── */
                <div className="text-center py-2">
                  <div style={{ fontSize: 48 }}>📧</div>
                  <h6 className="mt-3 fw-bold" style={{ color: "#001F54" }}>
                    Check Your Email
                  </h6>
                  <p className="text-muted small mb-1">
                    If <strong>{sentTo}</strong> is registered with us, a password
                    reset link has been sent to that address.
                  </p>
                  <p className="text-muted small mb-3">
                    The link will expire in <strong>60 minutes</strong>.
                  </p>
                  <button
                    type="button"
                    className="btn btn-sm w-100 text-white"
                    style={{ backgroundColor: "#001F54" }}
                    onClick={() => navigate("/login")}
                  >
                    Back to Login
                  </button>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="footer text-white py-3">
        <div className="ps-5">
          © 2026 <span className="fw-bold">OneFinCore</span> All Rights Reserved.
        </div>
      </footer>

      {/* ERROR POPUP */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h5 className="text-danger text-center">⚠ Error</h5>
            <p className="text-center">{error}</p>
            <button
              className="btn btn-danger w-100"
              type="button"
              onClick={() => { setShowPopup(false); setError(""); }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForgotPassword;